import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, apiError } from "@/components/admin/api";
import BookingCard from "@/components/admin/BookingCard";
import { formatDateLong } from "@/pages/Agendar";

const StatCard = ({ label, value, testId, tone = "default" }) => (
  <div
    data-testid={testId}
    className={`rounded-3xl border p-6 ${
      tone === "gold" ? "border-gold-400/50 bg-gold-100/40" : "border-raiz-900/10 bg-paper"
    }`}
  >
    <p className={`font-serif text-4xl font-light ${tone === "gold" ? "text-gold-700" : "text-raiz-900"}`}>{value}</p>
    <p className="mt-2 text-xs uppercase tracking-[0.18em] text-ink/50">{label}</p>
  </div>
);

const PanelOverview = ({ onOpenPackage }) => {
  const [data, setData] = useState(null);
  const [acting, setActing] = useState(false);

  const load = useCallback(() => {
    adminApi
      .get("/admin/journey/overview")
      .then((res) => setData(res.data))
      .catch((e) => toast.error(apiError(e, "Não foi possível carregar o painel.")));
  }, []);

  useEffect(load, [load]);

  const onAction = async (protocol, payload) => {
    if (payload.action === "cancelar" && !window.confirm("Cancelar este agendamento?")) return;
    setActing(true);
    try {
      await adminApi.patch(`/admin/journey/bookings/${protocol}`, payload);
      toast.success("Agendamento atualizado.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActing(false);
    }
  };

  if (!data) {
    return <div className="h-64 animate-pulse rounded-3xl border border-raiz-900/10 bg-cream" data-testid="painel-loading" />;
  }

  return (
    <div className="space-y-10" data-testid="painel-overview">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <StatCard label="Atendimentos de hoje" value={data.today.length} testId="stat-hoje" />
        <StatCard label="Próximos (7 dias)" value={data.upcoming.length} testId="stat-proximos" />
        <StatCard label="Horários livres hoje" value={data.available_today.length} testId="stat-livres-hoje" />
        <StatCard label="Reservas temporárias" value={data.holds.length} testId="stat-reservas" />
        <StatCard label="Aguardando confirmação" value={data.awaiting_payment.length} testId="stat-aguardando" tone="gold" />
      </div>

      <section data-testid="painel-hoje">
        <h3 className="font-serif text-2xl font-light text-ink">Agenda de hoje</h3>
        {data.today.length === 0 ? (
          <p className="mt-4 rounded-2xl border border-raiz-900/10 bg-cream p-5 text-sm text-ink/60">
            Nenhum atendimento hoje. Horários livres: {data.available_today.length > 0 ? data.available_today.join(", ") : "nenhum"}.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {data.today.map((b) => (
              <BookingCard key={b.protocol} b={b} onAction={onAction} acting={acting} onOpenPackage={onOpenPackage} />
            ))}
          </div>
        )}
      </section>

      <section data-testid="painel-aguardando">
        <h3 className="font-serif text-2xl font-light text-ink">Aguardando sua confirmação</h3>
        {data.awaiting_payment.length === 0 ? (
          <p className="mt-4 rounded-2xl border border-raiz-900/10 bg-cream p-5 text-sm text-ink/60">
            Nenhum agendamento aguardando sua confirmação.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {data.awaiting_payment.map((b) => (
              <BookingCard key={b.protocol} b={b} onAction={onAction} acting={acting} onOpenPackage={onOpenPackage} />
            ))}
          </div>
        )}
      </section>

      {data.holds.length > 0 && (
        <section data-testid="painel-reservas">
          <h3 className="font-serif text-2xl font-light text-ink">Reservas temporárias ativas</h3>
          <div className="mt-4 space-y-2">
            {data.holds.map((h) => (
              <p key={h.hold_id} data-testid={`hold-${h.hold_id}`} className="rounded-2xl border border-gold-400/40 bg-gold-100/40 p-4 text-sm text-ink/75">
                {formatDateLong(h.date)} às {h.time} — reservado até{" "}
                {new Date(h.expires_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} enquanto o paciente conclui o agendamento.
              </p>
            ))}
          </div>
        </section>
      )}

      <section data-testid="painel-proximos">
        <h3 className="font-serif text-2xl font-light text-ink">Próximos atendimentos</h3>
        {data.upcoming.length === 0 ? (
          <p className="mt-4 rounded-2xl border border-raiz-900/10 bg-cream p-5 text-sm text-ink/60">
            Nenhum atendimento nos próximos 7 dias.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {data.upcoming.map((b) => (
              <BookingCard key={b.protocol} b={b} onAction={onAction} acting={acting} onOpenPackage={onOpenPackage} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default PanelOverview;
